import React, { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import classNames from "classnames";
import { Range } from "react-range";
import useOnClickOutside from "../../../hooks/useOnClickOutside";
// import Button from "../../../components/Button";
import {
  selectSecondaryFilter,
  updateSecondaryFilter,
} from "../secondaryFilterSlice";
import translate from "../../../utilities/translator";
import { scrollToResults } from "../../../utilities/helpers";
import { pushEvent } from "../../../utilities/analytics";
import {
  DURATION_FILTER_UPDATE_EVENT,
  SEC_FILTER_OPEN,
  REFUNDABILITY_FILTER_UPDATE_EVENT,
} from "../../../constants/analyticEvents";
// import { clearSelectedHotel } from "../../search/searchSlice";
import Checkbox from "../../../components/forms/Checkbox";
import RangeThumb from "../../../components/ui/range/RangeThumb";
import RangeTrack from "../../../components/ui/range/RangeTrack";
import useStickFilterPosition from "../../../hooks/useStickFilterPosition";
import { filterClearClick, filterSaveClick, setLastChangedFilter } from '../../../appSlice';
import { clearPrefetchDeals } from '../../dealsList/prefetchDealsSlice';
import abTesting from '../../../services/abTesting';

export default function FlightDuration({ isOpen, setIsOpen, addFilterToSet }) {
  const ref = useRef();
  const popupRef = useRef();
  const dispatch = useDispatch();
  const { data } = useSelector(selectSecondaryFilter);
  const { duration, flex } = data;
  const { activeTime, minTime, maxTime } = duration;

  const [localState, setLocalState] = useState([activeTime / 60]);
  const isRtl = document.querySelector('html').getAttribute('dir') === 'rtl'
  const filterPositionClassName = useStickFilterPosition(popupRef, ref, isOpen);
  const [localFlexState, setLocalFlexState] = useState(flex);

  useOnClickOutside(ref, () => isOpen && setIsOpen(false));

  const onSaveClick = (event) => {
    setIsOpen(false);
    dispatch(filterSaveClick());
    dispatch(clearPrefetchDeals());
    dispatch(
      updateSecondaryFilter({
        updatedValue: { activeTime: localState * 60, minTime, maxTime },
        filter: 'duration',
      })
    );
    if (localFlexState && localFlexState.available && !abTesting.moveMultiRates) {
      dispatch(
        updateSecondaryFilter({
          updatedValue: localFlexState,
          filter: 'flex'
        })
      );
      pushEvent(REFUNDABILITY_FILTER_UPDATE_EVENT, { flex: localFlexState, type: 'save' });
    }
    dispatch(setLastChangedFilter('secondaryFilter'));
    event?.stopPropagation();
    pushEvent(DURATION_FILTER_UPDATE_EVENT, { activeTime: localState * 60, minTime, maxTime });
    // dispatch(clearSelectedHotel());
    scrollToResults();
  };

  window.external_set_flight_duration = (setFlightDuration) => {
    addFilterToSet(() => {
      setIsOpen(false);
      dispatch(filterSaveClick());
      dispatch(clearPrefetchDeals());
      dispatch(
        updateSecondaryFilter({
          updatedValue: { activeTime: setFlightDuration * 60, minTime, maxTime },
          filter: 'duration',
        })
      );
      pushEvent(DURATION_FILTER_UPDATE_EVENT, {
        activeTime: setFlightDuration * 60,
        minTime,
        maxTime
      });
      // dispatch(clearSelectedHotel());
      scrollToResults();
    });
  };

  const onClearClick = (event) => {
    setIsOpen(false);
    dispatch(filterClearClick());
    const disable = { activeTime: maxTime, minTime, maxTime };
    setLocalState([maxTime / 60]);
    dispatch(updateSecondaryFilter({
      updatedValue: disable, filter: "duration"
    }));
    if (localFlexState && localFlexState.available && !abTesting.moveMultiRates) {
      setLocalFlexState({
        ...localFlexState,
        active: false
      });
      dispatch(
        updateSecondaryFilter({
          updatedValue: {
            ...localFlexState,
            active: false
          },
          filter: 'flex'
        })
      );
    }
    event?.stopPropagation();
    scrollToResults();
    pushEvent(DURATION_FILTER_UPDATE_EVENT, { ...disable });
  };

  window.external_reset_flight_duration = () => {
    addFilterToSet(() => {
      onClearClick();
    });
  };

  const openFilter = (st) => {
    if (!isOpen) {
      pushEvent(SEC_FILTER_OPEN, { filterName: 'duration' });
    }
    setIsOpen(st);
  };

  const changeFlex = () => {
    const newFlexState = {
      ...localFlexState,
      active: !localFlexState.active
    };
    setLocalFlexState(newFlexState);
  };

  useEffect(() => {
    setLocalState([activeTime / 60]);
  }, [duration, activeTime]);

  const isActiveFilter = (activeTime < maxTime) || (localFlexState.active && !abTesting.moveMultiRates);

  return (
    <div className="filter-item filter-item-v2 filter-item--collapsed" ref={ref}>
      <p
        className={classNames('filter-item__name', {
          'filter-item__name-open': isOpen,
          'filter-item__name-active': isActiveFilter,
        })}
        onClick={() => openFilter(!isOpen)}
        role="menuitem"
        tabIndex="0"
      >
        <i className="ri-more-2-line" />
      </p>
      {isOpen && (
        <div className={`filter-body filter-body-v2 ${filterPositionClassName}`} style={{ minWidth: "383px" }} ref={popupRef}>
          <div className="flex flex-col">
            <span className="filter-item__title">{translate('holidayFinder.secondaryFilter.flightDuration')}</span>
            <div className="duration__wrap">
              <Range
                step={1}
                min={minTime / 60}
                max={maxTime / 60}
                values={localState}
                rtl={isRtl}
                onChange={(values) => setLocalState(values)}
                renderTrack={({ props, children }) => (
                  <RangeTrack
                    min={minTime}
                    max={maxTime}
                    values={localState.map((item) => item * 60)}
                    trackProps={props}
                  >
                    {children}
                  </RangeTrack>
                )}
                renderThumb={props => <RangeThumb {...props} />}
              />
              <div className="duration__range-values">
                <div className="duration__range-min">
                  <div className="duration-label">
                    {" "}
                    {translate("holidayFinder.secondaryFilter.under")}
                  </div>
                  <div className="duration-value">
                    {`${localState[0]} ${translate("holidayFinder.secondaryFilter.hours_plural", { count: localState[0] })}`}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {localFlexState && localFlexState.available && !abTesting.moveMultiRates && <div className='cancellation'>
            <span className='filter-item__title'>{translate('holidayFinder.secondaryFilter.cancellationFlexibility.title')}</span>
            <Checkbox
              onChange={changeFlex}
              label={translate('holidayFinder.secondaryFilter.refundableWithFlex')}
              checked={localFlexState.active}
            />
          </div>}

          <div className="filter-footer-v2 flex gap-3">
            <button className="panel-button panel-button__clear py-4 px-6 rounded-full" onClick={onClearClick}>
              {translate('holidayFinder.buttons.Clear')}
            </button>
            <button className="panel-button panel-button__save py-4 px-6 rounded-full" onClick={onSaveClick}>
              {translate('holidayFinder.buttons.Save')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
